import { Cell, Row } from '@dexma/ui-components';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { OPTIONS } from 'adapter/in/common/widget/options';
import {
    handleResetFields,
    setShowResetByData,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group';
import {
    StyledOptionContainer,
    StyledResetButton,
    StyledResetOptionContainer,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group/style';
import MinMaxGroupContainer from 'adapter/in/dashboards/pages/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/OptionForm/MinMax';
import i18n from 'asset/translation';
import Widget from 'domain/entity/widget';
import GaugeData from 'domain/entity/widgets/GaugeData';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { ConfigFieldType, FieldStatus, TextInputFieldData } from 'adapter/in/common/form/types';

type MinMaxContainerProps = {
    data: GaugeData;
    title: string;
};

export const getCustomMin = (widget: Widget, widgetData?: GaugeData): TextInputFieldData => {
    const minOptionValue = widget.optionFields.filter((option) => option.key === OPTIONS.MIN);
    if (widgetData && minOptionValue.length === 0) {
        const isDefaultMin = widgetData !== undefined && minOptionValue.length === 0;
        const valueMin = isDefaultMin ? String(widgetData.min) : minOptionValue[0].value;
        return buildTextInputFieldData(
            isDefaultMin ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE,
            String(valueMin),
            ConfigFieldType.OPTIONS,
            String(valueMin)
        );
    }
    return buildTextInputFieldData(
        minOptionValue.length === 0 ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE,
        minOptionValue.length === 0 ? String(0) : String(minOptionValue[0].value),
        ConfigFieldType.OPTIONS,
        minOptionValue.length === 0 ? String(0) : String(minOptionValue[0].value)
    );
};

export const getCustomMax = (widget: Widget, widgetData?: GaugeData): TextInputFieldData => {
    const maxOptionValue = widget.optionFields.filter((option) => option.key === OPTIONS.MAX);
    if (widgetData && maxOptionValue.length === 0) {
        const isDefaultMax = widgetData !== undefined && maxOptionValue.length === 0;
        const valueMax = isDefaultMax ? String(widgetData.max) : maxOptionValue[0].value;
        return buildTextInputFieldData(
            isDefaultMax ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE,
            String(valueMax),
            ConfigFieldType.OPTIONS,
            String(valueMax)
        );
    }
    return buildTextInputFieldData(
        maxOptionValue.length === 0 ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE,
        maxOptionValue.length === 0 ? String(1) : String(maxOptionValue[0].value),
        ConfigFieldType.OPTIONS,
        maxOptionValue.length === 0 ? String(1) : String(maxOptionValue[0].value)
    );
};

const MinMaxContainer = ({ data, title }: MinMaxContainerProps): ReactElement => {
    const fieldKeys = [OPTIONS.MIN, OPTIONS.MAX];
    const { getValues, setValue, watch, clearErrors } = useFormContext<FieldValues>();
    const watchedFields = watch(fieldKeys);
    const [showReset, setShowReset] = useState<boolean>(false);
    const getValue = useCallback((fieldKey: string) => getValues(fieldKey), [watchedFields]);

    useEffect(() => {
        if (!watchedFields.includes(undefined) && !watchedFields.map((value) => value.status).includes(FieldStatus.INITIAL_VALUE)) {
            setShowResetByData(fieldKeys, data, watchedFields, setShowReset, showReset);
        }
    }, [fieldKeys, data, showReset, watchedFields]);

    return (
        <StyledOptionContainer key='minmax_options' data-testid='minmax_options'>
            <Row style={{ padding: '10px 22px 0px 30px' }}>
                <Cell className='widget-type-block'>
                    <span>{title}</span>
                </Cell>
                {showReset && (
                    <StyledResetOptionContainer>
                        <StyledResetButton
                            type='button'
                            variant='link'
                            text={i18n.t('widgets.widgetMenu.resetDefaultData')}
                            data-testid='cancel'
                            onClick={() => handleResetFields(setShowReset, getValues, setValue, data, fieldKeys, clearErrors)}
                        />
                    </StyledResetOptionContainer>
                )}
            </Row>
            <MinMaxGroupContainer getValue={getValue} />
        </StyledOptionContainer>
    );
};

export default MinMaxContainer;
